//@ts-nocheck
import groupBy from "lodash.groupby";
export function unansweredQs(answers = {}, questions = []) {
  let questionsAnswered = Object.keys(answers).map((num) => parseInt(num));
  let questionNumbers = questions.map((_, index) => index + 1);
  let unansweredQuestions = questionNumbers.filter(
    (num) => !questionsAnswered.includes(num),
  );
  let completionRate = (questionsAnswered.length / questions.length) * 100;
  return { unansweredQuestions, completionRate, questionsAnswered };
}

/**
 * @file Quiz Grader
 *
 * Grades the quiz answered by clients and provides data about whether user passed or failed.
 *
 * @param {Object} inputAnswers - Object of answers supplied by user
 * @param {Array} quiz - the quiz to be graded
 *
 * @returns {Object} Returns an object containing questionsAnswered, completionRate, correct answers, passrate and hasPassed.
 *
 * @example
 * (inputAnswers, quiz)
 * //=> {correctAnswers: 1, passRate: 60, hasPassed: true}
 */
export function quizGrader(inputAnswers = {}, quiz = {}) {
  let { questions, pass_mark } = quiz as {
    questions: Array<any>;
    pass_mark: number;
  };
  let { questionsAnswered, completionRate } = unansweredQs(
    inputAnswers,
    questions,
  );
  let correctAnswers = 0;
  let options = {
    0: "A",
    1: "B",
    2: "C",
    3: "D",
  };

  questions.forEach(({ answers }, index) => {
    let correctIndex = answers.findIndex(({ correct }) => Boolean(correct));
    let correctOption = correctIndex > -1 && options[correctIndex];
    inputAnswers[index]?.answer === correctOption && correctAnswers++;
  });

  let passRate = (correctAnswers / questions.length) * 100;
  let passedQuiz = passRate >= pass_mark;

  return {
    questionsAnswered: questionsAnswered.length,
    completionRate,
    correctAnswers,
    passRate,
    passedQuiz,
  };
}

export function gradeQuiz(
  quizzes: Array<{
    name: string;
    questions: Array<any>;
    passmark: number;
  }>,
  answers: Array<{ question_id: number; answer: any }>,
  question_count: number,
  testedSubjects: Array<{ name: string; pass_mark: number }>,
): {
  passed: boolean;
  avgPassmark: number;
  totalQuizGrade: number;
  result: Array<{
    score: number;
    passed: boolean;
    passmark: number;
    subject: string;
  }>;
  questionsAnswered?: number;
  completionRate?: number;
  passRate?: number;
  passedQuiz?: boolean;
  correctAnswers?: number;
} {
  /**This function is an internal function, you would need to make api calls to get
   * the quiz data.
   */
  let avgPassmark = sum(quizzes.map((o) => o.passmark)) / quizzes.length;
  // group the answers into their corresponding quizes
  let combinedQuestions = quizzes
    .map((quiz) => {
      return quiz.questions.map((o) => ({ ...o, subject: quiz.name }));
    })
    .flat();
  let transformedAnswers = answers.map((a) => {
    let found = combinedQuestions.find((o) => o.id === a.question_id);
    let isCorrect = false;
    let subject = null;
    if (found) {
      isCorrect = found.answers[a.answer].correct === true;
      subject = found.subject;
    }
    return { ...a, correct: isCorrect, subject };
  });
  let correctAnswers = transformedAnswers.filter((o) => o.correct).length;
  let passedQuizAvg = (correctAnswers * 100) / question_count;
  let graded = groupBy(transformedAnswers, "subject");
  let result = {};
  Object.keys(graded).forEach((gr) => {
    let key = gr;
    let quizInstance = quizzes.find((o) => o.name === gr);
    let value = graded[gr];
    let score = (value.filter((o) => o.correct).length * 100) / value.length;

    result[key] = {
      score,
      passed: score > quizInstance.passmark,
      passmark: quizInstance.passmark,
    };
  });
  if (Object.keys(result).length === 0) {
    // Not sure why this logic exists.
    testedSubjects.forEach((subject) => {
      if (!result.hasOwnProperty(subject.name)) {
        result[subject.name] = {
          score: 0,
          passed: false,
          passmark: subject.pass_mark,
        };
      }
    });
  }
  return {
    avgPassmark,
    totalQuizGrade: passedQuizAvg,
    result: Object.keys(result).map((o) => ({ ...result[o], subject: o })),
    passed: passedQuizAvg >= avgPassmark,
    questionsAnswered: answers.length,
    completionRate: (answers.length / question_count) * 100,
    passRate: avgPassmark,
    passedQuiz: passedQuizAvg >= avgPassmark,
    correctAnswers,
  };
}
function sum(array: number[]) {
  return array.reduce((a, b) => a + b, 0);
}
